import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import {
  CreateInvoiceRequest,
  DesktopInvoiceDetails,
  InvoicesService,
} from 'src/app/services/invoices.service';
import {
  DesktopLicenseWithProduct,
  LicensesService,
} from 'src/app/services/licenses.service';
import { Router } from '@angular/router';
import {
  ProductsService,
  ProductVariant,
} from 'src/app/services/products.service';
import { HttpErrorResponse } from '@angular/common/http';
import {
  SalesRep,
  SalesRepsService,
} from 'src/app/services/sales-reps.service';

type DisplayLicenses = {
  checked: boolean;
  status: string;
  license: DesktopLicenseWithProduct;
  selectedDurationId: string;
  manualPrice?: number;
  manualExpiration?: string;
};

@Component({
  selector: 'app-create-invoice',
  templateUrl: './create-invoice.component.html',
  styleUrl: './create-invoice.component.scss',
})
export class CreateInvoiceComponent implements OnInit, OnChanges {
  @Input() desktopLicenses?: {
    active: DesktopLicenseWithProduct[];
    expired: DesktopLicenseWithProduct[];
    pending: DesktopLicenseWithProduct[];
  };
  @Input() desktopInvoices?: {
    pending: DesktopInvoiceDetails[];
    complete: DesktopInvoiceDetails[];
    expired: DesktopInvoiceDetails[];
  };

  @Output() invoiceCreated = new EventEmitter<DesktopInvoiceDetails>();
  @Output() closeDialog = new EventEmitter<void>();

  creatingInvoice = false;
  createInvoiceFilters = {
    active: true,
    pending: true,
    expired: false,
  };
  validInvoiceLicenses?: DisplayLicenses[];
  promptDuration = true;
  errorCreatingInvoice = '';
  licensesIdsForInvoice: string[] = [];
  useManualValues = false;

  fetchingProductVariantDurations = false;
  productVariantDurations: { [prop: string]: ProductVariant[] } = {};
  errorFetchingProductVariantDurations = '';

  fetchingSalesReps = false;
  salesReps: SalesRep[] = [];
  errorFetchingSalesReps = '';
  selectedSalesReps?: SalesRep[];
  invoiceNote = '';

  constructor(
    private invoicesService: InvoicesService,
    private licensesService: LicensesService,
    private router: Router,
    private productsService: ProductsService,
    private salesRepsService: SalesRepsService
  ) {}

  ngOnInit(): void {
    this.filterValidInvoiceLicenses();
    // for each of the licenses, get all of the potential product variants to
    // be used as options for the duration selection
    this.fetchProductVariantDurations();
    this.fetchSalesReps();
  }

  ngOnChanges(): void {
    this.filterValidInvoiceLicenses();
    this.fetchProductVariantDurations();
  }

  get availableSalesReps() {
    return this.salesReps.filter((r) => !this.selectedSalesReps?.includes(r));
  }

  selectAllLicenses(event: Event) {
    console.log('selecting all licenses');
    const checked = (event.target as HTMLInputElement).checked;
    this.validInvoiceLicenses?.forEach((l) => {
      l.checked = checked;
    });
  }

  removeSalesRep(salesRep: SalesRep) {
    console.log('removing sales rep: ', salesRep);
    if (this.selectedSalesReps) {
      this.selectedSalesReps = this.selectedSalesReps.filter(
        (r) => r.id !== salesRep.id
      );
    }
  }

  addSalesRep(salesRep: SalesRep) {
    console.log('adding sales rep: ', salesRep);
    if (!this.selectedSalesReps) {
      this.selectedSalesReps = [];
    }
    if (!this.selectedSalesReps.includes(salesRep)) {
      this.selectedSalesReps.push(salesRep);
    }
  }

  getLicensePrice(license: DesktopLicenseWithProduct) {
    return this.licensesService.getPrice(license);
  }

  getLicenseType(license: DesktopLicenseWithProduct) {
    return this.licensesService.getType(license);
  }

  fetchProductVariantDurations() {
    console.log('fetching product variant durations');
    this.fetchingProductVariantDurations = true;
    this.errorFetchingProductVariantDurations = '';

    const allLicenses = [];
    if (this.desktopLicenses) {
      allLicenses.push(...this.desktopLicenses.active.map((l) => l));
      allLicenses.push(...this.desktopLicenses.pending.map((l) => l));
      allLicenses.push(...this.desktopLicenses.expired.map((l) => l));
    }

    if (allLicenses.length === 0) {
      this.fetchingProductVariantDurations = false;
      return;
    }

    let cnt = 0;

    for (const license of allLicenses) {
      // if the product variant durations have already been fetched, skip
      if (this.productVariantDurations[license.product_variant.id]) {
        continue;
      }
      console.log('fetching product variant durations for license: ', license);
      cnt++;
      this.productsService
        .getDurationOptions(license.product_variant)
        .subscribe({
          next: (data) => {
            data.product_variants.sort(
              (a, b) => a.duration_months - b.duration_months
            );
            this.productVariantDurations[license.product_variant.id] =
              data.product_variants;
            cnt--;
            if (cnt === 0) {
              console.log('done fetching product variant durations');
              this.fetchingProductVariantDurations = false;
            }
          },
          error: (err) => {
            cnt--;
            if (cnt === 0) {
              console.log('done fetching product variant durations');
              this.fetchingProductVariantDurations = false;
            }
            if (this.errorFetchingProductVariantDurations === '') {
              this.errorFetchingProductVariantDurations =
                'Error fetching product variant duration options';
            }
            console.error(err);
          },
        });
    }
  }

  fetchSalesReps() {
    this.fetchingSalesReps = true;
    this.errorFetchingSalesReps = '';
    this.salesReps = [];

    this.salesRepsService.getAllSalesReps().subscribe({
      next: (salesReps) => {
        this.fetchingSalesReps = false;
        this.errorFetchingSalesReps = '';
        this.salesReps = salesReps;
      },
      error: (err) => {
        this.fetchingSalesReps = false;
        console.error(err);
        this.errorFetchingSalesReps = 'Error fetching sales reps';
      },
    });
  }

  getDurationOptions(productVariantId: string) {
    return this.productVariantDurations[productVariantId];
  }

  filterValidInvoiceLicenses() {
    let all: DisplayLicenses[] = [];
    if (this.desktopLicenses?.active && this.createInvoiceFilters.active) {
      all.push(
        ...this.desktopLicenses.active.map((l) => {
          const initialPrice = this.getLicensePrice(l);
          const initialExpiration = l.expiration
            ? new Date(l.expiration).toISOString().split('T')[0]
            : undefined;
          return {
            checked: false,
            status: 'Active',
            license: l,
            selectedDurationId: l.product_variant.id,
            manualPrice: initialPrice,
            manualExpiration: initialExpiration,
          };
        })
      );
    }
    if (this.desktopLicenses?.expired && this.createInvoiceFilters.expired) {
      all.push(
        ...this.desktopLicenses.expired.map((l) => {
          const initialPrice = this.getLicensePrice(l);
          const initialExpiration = l.expiration
            ? new Date(l.expiration).toISOString().split('T')[0]
            : undefined;
          return {
            checked: false,
            status: 'Expired',
            license: l,
            selectedDurationId: l.product_variant.id,
            manualPrice: initialPrice,
            manualExpiration: initialExpiration,
          };
        })
      );
    }
    if (this.desktopLicenses?.pending && this.createInvoiceFilters.pending) {
      all.push(
        ...this.desktopLicenses.pending.map((l) => {
          const initialPrice = this.getLicensePrice(l);
          const initialExpiration = l.expiration
            ? new Date(l.expiration).toISOString().split('T')[0]
            : undefined;
          return {
            checked: false,
            status: 'Pending',
            license: l,
            selectedDurationId: l.product_variant.id,
            manualPrice: initialPrice,
            manualExpiration: initialExpiration,
          };
        })
      );
    }

    // filter out licenses already used in pending invoices
    if (this.desktopInvoices?.pending) {
      const pendingInvoices = this.desktopInvoices.pending;
      all = all.filter((license) => {
        return !pendingInvoices.find((invoice) => {
          return invoice.license_details.find(
            (l) => l.license_with_product.id === license.license.id
          );
        });
      });
    }

    this.validInvoiceLicenses = all;
  }

  createInvoice() {
    const licensesForInvoice = this.validInvoiceLicenses?.filter(
      (l) => l.checked
    );

    console.log('licensesForInvoice: ', licensesForInvoice);

    if (!licensesForInvoice || licensesForInvoice.length === 0) {
      this.errorCreatingInvoice = 'No licenses selected for invoice';
      return;
    }

    console.log('promptDuration: ', this.promptDuration);
    this.creatingInvoice = true;
    const request: CreateInvoiceRequest = {
      licenses: licensesForInvoice.map((l) => {
        const licenseRequest: any = {
          desktop_license_id: l.license.id,
          new_product_variant_id: l.selectedDurationId,
        };
        // Include manual values if manual mode is enabled
        if (this.useManualValues) {
          if (l.manualPrice !== undefined) {
            licenseRequest.manual_price = l.manualPrice;
          }
          if (l.manualExpiration) {
            // Create full ISO date string with time set to midnight UTC
            const date = new Date(l.manualExpiration + 'T00:00:00Z');
            licenseRequest.manual_expiration = date.toISOString();
          }
        }
        return licenseRequest;
      }),
      initialized: !this.promptDuration,
    };

    if (this.selectedSalesReps) {
      request.sales_rep_ids = this.selectedSalesReps.map((r) => r.id);
    }

    const trimmedNote = this.invoiceNote.trim();
    if (trimmedNote) {
      request.notes = trimmedNote;
    }

    console.log('create invoice request: ', request);

    this.invoicesService.createInvoice(request).subscribe({
      next: (res) => {
        this.creatingInvoice = false;
        console.log('res: ', res);
        if (res.invoice_details) {
          this.invoiceCreated.emit(res.invoice_details);
          this.navigateToInvoice(
            res.invoice_details.desktop_invoice.tenant_id,
            res.invoice_details.desktop_invoice.id
          );
        } else {
          console.error(new Error('No invoice_details returned from server'));
          this.errorCreatingInvoice = 'Server error creating invoice';
        }
      },
      error: (err) => {
        this.creatingInvoice = false;
        console.error(err);

        if (err instanceof HttpErrorResponse) {
          if (err.error.error === 'invalid address') {
            // tenant does not have a valid address
            this.errorCreatingInvoice =
              'Tenant does not have a valid address. Unable to create invoice.';
          } else {
            this.errorCreatingInvoice = err.error.error;
          }
        } else {
          this.errorCreatingInvoice = 'Server error creating invoice';
        }
      },
    });
  }

  navigateToInvoice(tenantId: string, invoiceId: string) {
    console.log('navigate to invoice', tenantId, invoiceId);
    this.router.navigate(['/admin/tenant/', tenantId, 'invoice', invoiceId]);
  }
}
